import { Destination } from './tenant';
import { CountryCode } from './country';

export interface LoadingPlace {
    loadingType: 'LOADING' | 'UNLOADING';
    address: Address;
}

export interface CurrentBooking {
    id: number;
    vehicle_id: number;
    loadingPlaces: LoadingPlace[];
    startTime: Date | string;
    endTime: Date | string;
    internal_offer_id: number | null;
    externalOfferId: string | null;
    status: number;
    distanceKms: number | null;
    price: number | null;
    createdAt: Date | string;
    updatedOn: Date | string;
    customerDomain: string | null;
    score: number | null;
    tenant_id: number;
    source: number;
    routeLegs: any | null;
    startPlace: any | null;
    endPlace: any | null;
}

export interface Vehicle {
    id: number;
    truckPlate: string;
    trailerPlate: string;
    name: string;
    manufacturer: string;
    model: string;
    modelYear: number;
    colour: string;
    chassisNumber: string;
    type: VehicleType;
    swappable: boolean;
    swapBodyType: SwappableBodyType;
    trailerProperty: TrailerProperty[];
    trailerType: TrailerType;
    equipment: TrailerEquipment[];
    loadSecurity: LoadSecurity[];
    currentPosition: Address;
    emptyIn: Address;
    emptyAt: Date;
    isActive: boolean;
    createdAt: Date;
    tenant_id: number;
    lastTimeSearchedAt: Date;
    tenantOverrides: TenantOverrides;
    tenantPreferredRoute: string;
    maximumWeightT: number;
    maximumLengthM: number;
    regCountry: string; // In ISO2 format
    maximumCabotages: number;
    tracker: TrackerProvider;
    currentPositionSyncedAt: Date;
    currentPositionNextSyncAt: Date;
    currentPositionAutoSyncIntervalMin: number;
    maxDailyAutoSearches: number;
    maxDailyAutoNegos: number;
    current_booking_id?: number; // Booking ID we want to search after
    manager_user_id?: number; // User ID of the manager who manages this vehicle
    currentBooking?: CurrentBooking; // Current booking data when with_current_booking is true
}

export interface VehicleWithDriver extends Vehicle {
  driverFirstName?: string;
  driverLastName?: string;
  driverId?: number;
  driverAvailableHours?: number;
}

export interface TenantOverrides {
    destinationsToExclude?: {country: CountryCode, codes: string[]}[];
    preferredDestinations?: Record<string, Destination[]>;
    allowAutoValidate?: boolean;
}

export interface VehicleProperties {
  body: TrailerType[];
  bodyProperty: TrailerProperty[];
  equipment: TrailerEquipment[];
  loadSecuring: LoadSecurity[];
  swapBody: SwappableBodyType[]
  type: VehicleType[];
}

export interface Address {
  objectType: string;
  city: string;
  country: CountryCode;
  geoCoordinate: GeoCoordinate;
  geocoded: boolean;
  postalCode: string;
  streetOrPostbox: string | null;
}

export interface GeoCoordinate {
  longitude: number;
  latitude: number;
}

export type TrackerProvider = 'CartrackTracker' | 'SamsaraTracker';
export const TrackerProviders: TrackerProvider[] = ['CartrackTracker', 'SamsaraTracker'];

export enum VehicleType {
    TRAILER = 'TRAILER',
    VEHICLE_UP_TO_12_T = 'VEHICLE_UP_TO_12_T',
    VEHICLE_UP_TO_3_5_T = 'VEHICLE_UP_TO_3_5_T',
    VEHICLE_UP_TO_7_5_T = 'VEHICLE_UP_TO_7_5_T',
    WAGGON_AND_DRAG = 'WAGGON_AND_DRAG'
}

export enum SwappableBodyType {
    FORTY_FEET_CONTAINER = 'FORTY_FEET_CONTAINER',
    FORTY_FIVE_FEET_CONTAINER = 'FORTY_FIVE_FEET_CONTAINER',
    HALFPIPE_DUMPER = 'HALFPIPE_DUMPER',
    ROLL_ON_ROLL_OFF_CONTAINER = 'ROLL_ON_ROLL_OFF_CONTAINER',
    SWAP_BODY = 'SWAP_BODY',
    TWENTY_FEET_CONTAINER = 'TWENTY_FEET_CONTAINER',
}

export enum TrailerProperty {
    AIR_SUSPENDED = 'AIR_SUSPENDED',
    BACK_TIPPER = 'BACK_TIPPER',
    CODE_XL = 'CODE_XL',
    DOUBLE_EVAPORATOR = 'DOUBLE_EVAPORATOR',
    DOUBLE_FLOOR = 'DOUBLE_FLOOR',
    ELEVATING_ROOF = 'ELEVATING_ROOF',
    FOLDING_SIDE_BOX = 'FOLDING_SIDE_BOX',
    HANGING_GARMENT_CONTAINER = 'HANGING_GARMENT_CONTAINER',
    LONG_TRUCK = 'LONG_TRUCK',
    LOW_FLOOR = 'LOW_FLOOR',
    LOW_LOADING_RAILS = 'LOW_LOADING_RAILS',
    REMOVAL_VAN = 'REMOVAL_VAN',
    SLIDING_ROOF = 'SLIDING_ROOF',
    SLIDING_CURTAIN = 'SLIDING_CURTAIN',
    SIDE_TIPPER = 'SIDE_TIPPER',
    WIDENABLE = 'WIDENABLE',
}

export enum TrailerType {
    BOX = 'BOX',
    CAR_TRANSPORTER = 'CAR_TRANSPORTER',
    CHASSIS = 'CHASSIS',
    COIL_WELL = 'COIL_WELL',
    CURTAIN_SIDER = 'CURTAIN_SIDER',
    DRAWER = 'DRAWER',
    DUMP_TRAILER = 'DUMP_TRAILER',
    EXTENDABLE_TRAILER = 'EXTENDABLE_TRAILER',
    FLATBED = 'FLATBED',
    INLOADER = 'INLOADER',
    JUMBO = 'JUMBO',
    LOW_LOADER = 'LOW_LOADER',
    MEGA = 'MEGA',
    MOVING_FLOOR = 'MOVING_FLOOR',
    MOVING_FLOOR_FILL_MATERIAL = 'MOVING_FLOOR_FILL_MATERIAL',
    PLATFORM = 'PLATFORM',
    REFRIGERATOR = 'REFRIGERATOR',
    SEMI_TRAILER_WITH_INCLINED_TABLE = 'SEMI_TRAILER_WITH_INCLINED_TABLE',
    SILO = 'SILO',
    SPECIAL_TRUCK = 'SPECIAL_TRUCK',
    SWAP_BODY_TRUCK = 'SWAP_BODY_TRUCK',
    TANK = 'TANK',
    TAUTLINER = 'TAUTLINER',
    THERMO = 'THERMO',
    TIPPER_ROLL_OFF = 'TIPPER_ROLL_OFF',
    TRACTOR_UNIT = 'TRACTOR_UNIT',
    VAN_CAR = 'VAN_CAR',
}

export enum TrailerEquipment {
    ACCESS_RAMP = 'ACCESS_RAMP',
    ADR_EQUIPMENT_SET = 'ADR_EQUIPMENT_SET',
    A_PLATE = 'A_PLATE',
    CUSTOMS_SEAL_STRING = 'CUSTOMS_SEAL_STRING',
    ESCORT_VEHICLE_TYPE_3 = 'ESCORT_VEHICLE_TYPE_3',
    ESCORT_VEHICLE_TYPE_4 = 'ESCORT_VEHICLE_TYPE_4',
    FIXED_LOADING_CRANE = 'FIXED_LOADING_CRANE',
    MEAT_HOOK = 'MEAT_HOOK',
    PARTITION_WALL = 'PARTITION_WALL',
    PORTABLE_FORKLIFT = 'PORTABLE_FORKLIFT',
    PORTABLE_PUMP_TRUCK = 'PORTABLE_PUMP_TRUCK',
    SATELLITE_TRACKING = 'SATELLITE_TRACKING',
    SECOND_DRIVER = 'SECOND_DRIVER',
    TAIL_LIFT = 'TAIL_LIFT',
    TARPAULIN_COVER = 'TARPAULIN_COVER',
    WOOD_STANCHIONS = 'WOOD_STANCHIONS',
}

export enum LoadSecurity {
    ANTI_SLIP_MATS = 'ANTI_SLIP_MATS',
    BOARD_WALL = 'BOARD_WALL',
    EDGE_PROTECTION = 'EDGE_PROTECTION',
    LASHING_STRAPS = 'LASHING_STRAPS',
    LOCKING_BAR = 'LOCKING_BAR',
    PALLET_STOP_BAR = 'PALLET_STOP_BAR',
    PERFORATED_BATTEN = 'PERFORATED_BATTEN',
    STANCHIONS = 'STANCHIONS',
    TENSION_CHAIN = 'TENSION_CHAIN',
}
